// src/publish/pieces/emailClassic.js — pieces of the classic email (format key 'email-email-classic').
//
// Backend fields (proposal, «Cosa manda il backend»): `subject` (mirrored in
// `title`), `hook`, `body` (inline HTML: <ul><li>, <strong>…), `cta_button`
// {text, url}, `signoff` (lines separated by "\n"), `ps_line`, `cta_url`;
// `cta` is empty and `hashtags` is []. Pieces, in this order:
//   body     PRIMARY, rich — the hook in italics, the body's paragraphs and
//            lists, the button as a link paragraph, the signoff (line breaks
//            kept inside one paragraph), the P.S. line
//   subject  plain, limit LIMITS.emailSubject (subject, else title)
//   button   plain — the button text and its URL as two paragraphs (what the
//            ESP's button widget asks for, one field each)
//   signoff  plain
//   ps       plain
// The button's URL is always the brief's (ctaUrlOf: content.cta_url, else
// params.cta_url); cta_button.url is only a fallback when the brief has none.
// An empty field produces no piece and no empty block; exactly one piece is
// primary (body, or the first piece when there is no body at all).
// i18n: outputs:pieces.labels["email-email-classic"].<id> and
// outputs:pieces.destinations["email-email-classic"].<id>.

import { normalizeText, inlineRuns } from '../normalize.js';
import { paragraph, em, link, text, compactBlocks } from '../blocks.js';
import { makePiece, ctaUrlOf, ensureOnePrimary, trimmed as str, obj, LIMITS } from '../piece.js';
import { normalizeUrl } from '../../utils/textProcessing.js';

const FORMAT = 'email-email-classic';

export function build(entry) {
  const content = obj(entry?.results?.content);
  if (!Object.keys(content).length) return [];

  const hook = str(content.hook);
  const subject = str(content.subject) || str(content.title);
  const signoff = str(content.signoff);
  const ps = str(content.ps_line);
  const button = obj(content.cta_button);
  const buttonText = str(button.text) || str(content.cta);
  const buttonUrl = ctaUrlOf(entry) || normalizeUrl(str(button.url));

  const body = [];
  if (hook) body.push(paragraph([em(inlineRuns(hook))]));
  body.push(...normalizeText(str(content.body)));
  if (buttonText) body.push(paragraph(buttonUrl ? [link(buttonUrl, [text(buttonText)])] : [text(buttonText)]));
  if (signoff) body.push(paragraph(inlineRuns(signoff)));
  if (ps) body.push(paragraph(inlineRuns(ps)));

  const pieces = [];
  const bodyBlocks = compactBlocks(body);
  if (bodyBlocks.length) pieces.push(makePiece(FORMAT, { id: 'body', kind: 'rich', blocks: bodyBlocks }));
  if (subject) {
    pieces.push(makePiece(FORMAT, { id: 'subject', blocks: [paragraph(inlineRuns(subject))], limit: LIMITS.emailSubject }));
  }
  if (buttonText) {
    const blocks = [paragraph(inlineRuns(buttonText))];
    if (buttonUrl) blocks.push(paragraph([text(buttonUrl)]));
    pieces.push(makePiece(FORMAT, { id: 'button', blocks }));
  }
  if (signoff) pieces.push(makePiece(FORMAT, { id: 'signoff', blocks: [paragraph(inlineRuns(signoff))] }));
  if (ps) pieces.push(makePiece(FORMAT, { id: 'ps', blocks: [paragraph(inlineRuns(ps))] }));

  return ensureOnePrimary(pieces, ['body']);
}
